/**
 * Media uploader component with drag-and-drop
 * Handles video and image file validation, upload progress, and thumbnail preview
 * Kept as VideoUploader.tsx to avoid import breakage across the codebase
 */

import { Upload, X, AlertCircle, Film, Image } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/Button';
import {
  getVideoDuration,
  getThumbnailDataUrl,
  isVideoFile,
  isImageFile,
  formatDuration,
} from '@/lib/thumbnailExtractor';
import { cn } from '@/lib/utils';
import {
  validateFileSize,
  validateFileType,
  formatFileSize,
} from '@/services/upload';
import type { MediaType } from '@/types';

// ============================================================================
// CONSTANTS
// ============================================================================

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ACCEPTED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm'];
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_VIDEO_SIZE_MB = 200;
const MAX_IMAGE_SIZE_MB = 20;
const MAX_DURATION_SECONDS = 180; // 3 minutes

const ALL_ACCEPTED_EXTENSIONS = [...ACCEPTED_VIDEO_EXTENSIONS, ...ACCEPTED_IMAGE_EXTENSIONS];

// ============================================================================
// TYPES
// ============================================================================

export interface VideoUploaderProps {
  projectId: string;
  onUploadComplete: (data: {
    file: File;
    duration: number;
    thumbnailUrl: string;
    mediaType: MediaType;
  }) => void;
  onError: (error: string) => void;
}

interface UploadState {
  file: File | null;
  duration: number | null;
  thumbnailUrl: string | null;
  mediaType: MediaType | null;
  progress: number;
  uploadSpeed: number;
  timeRemaining: number;
  uploading: boolean;
  error: string | null;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Read an image file as a data URL for preview
 */
function getImageDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read image as data URL'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// COMPONENT
// ============================================================================

export function VideoUploader({
  projectId: _projectId,
  onUploadComplete,
  onError,
}: VideoUploaderProps) {
  // _projectId will be used when integrating with API for upload URL generation
  const [state, setState] = React.useState<UploadState>({
    file: null,
    duration: null,
    thumbnailUrl: null,
    mediaType: null,
    progress: 0,
    uploadSpeed: 0,
    timeRemaining: 0,
    uploading: false,
    error: null,
  });

  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // ============================================================================
  // FILE VALIDATION
  // ============================================================================

  const validateFile = async (file: File): Promise<string | null> => {
    if (isImageFile(file)) {
      // Validate image file type
      if (
        !validateFileType(file, [
          ...ACCEPTED_IMAGE_TYPES,
          ...ACCEPTED_IMAGE_EXTENSIONS,
        ])
      ) {
        return 'Invalid image format. Please upload JPG, PNG, or WebP files only.';
      }

      // Validate image file size (20MB)
      if (!validateFileSize(file, MAX_IMAGE_SIZE_MB)) {
        return `Image size exceeds ${MAX_IMAGE_SIZE_MB}MB limit. Current size: ${formatFileSize(file.size)}`;
      }

      return null;
    }

    if (isVideoFile(file)) {
      // Validate video file type
      if (
        !validateFileType(file, [
          ...ACCEPTED_VIDEO_TYPES,
          ...ACCEPTED_VIDEO_EXTENSIONS,
        ])
      ) {
        return 'Invalid video format. Please upload MP4, MOV, or WebM files only.';
      }

      // Validate video file size (200MB)
      if (!validateFileSize(file, MAX_VIDEO_SIZE_MB)) {
        return `Video size exceeds ${MAX_VIDEO_SIZE_MB}MB limit. Current size: ${formatFileSize(file.size)}`;
      }

      // Validate duration
      try {
        const duration = await getVideoDuration(file);
        if (duration > MAX_DURATION_SECONDS) {
          return `Video duration exceeds ${MAX_DURATION_SECONDS / 60} minutes limit. Current duration: ${formatDuration(duration)}`;
        }
      } catch {
        return 'Failed to read video duration. The file may be corrupted.';
      }

      return null;
    }

    return 'Invalid file type. Please upload a video (MP4, MOV, WebM) or image (JPG, PNG, WebP).';
  };

  // ============================================================================
  // FILE PROCESSING
  // ============================================================================

  const processFile = async (file: File) => {
    setState((prev) => ({
      ...prev,
      file,
      error: null,
      uploading: true,
      progress: 0,
    }));

    try {
      // Validate file
      const validationError = await validateFile(file);
      if (validationError) {
        setState((prev) => ({ ...prev, error: validationError, uploading: false }));
        onError(validationError);
        return;
      }

      if (isImageFile(file)) {
        // Image: the image IS the thumbnail, no duration
        const thumbnailUrl = await getImageDataUrl(file);

        setState((prev) => ({
          ...prev,
          duration: null,
          thumbnailUrl,
          mediaType: 'image',
          uploading: false,
        }));

        // Notify parent (duration 0 for images, parent will handle)
        onUploadComplete({ file, duration: 0, thumbnailUrl, mediaType: 'image' });
      } else {
        // Video: extract duration and thumbnail
        const [duration, thumbnailUrl] = await Promise.all([
          getVideoDuration(file),
          getThumbnailDataUrl(file),
        ]);

        setState((prev) => ({
          ...prev,
          duration,
          thumbnailUrl,
          mediaType: 'video',
          uploading: false,
        }));

        // Notify parent component
        onUploadComplete({ file, duration, thumbnailUrl, mediaType: 'video' });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to process file';
      setState((prev) => ({ ...prev, error: errorMessage, uploading: false }));
      onError(errorMessage);
    }
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({
      file: null,
      duration: null,
      thumbnailUrl: null,
      mediaType: null,
      progress: 0,
      uploadSpeed: 0,
      timeRemaining: 0,
      uploading: false,
      error: null,
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const isImage = state.mediaType === 'image';

  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          'relative flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all duration-base',
          isDragging
            ? 'border-brand bg-brand/5'
            : state.error
              ? 'border-danger bg-danger/5'
              : 'border-border-default bg-surface-subtle hover:border-brand/50 hover:bg-brand/5',
          state.thumbnailUrl && 'bg-surface'
        )}
      >
        {state.thumbnailUrl ? (
          /* Preview */
          <div className="relative w-full p-8">
            <img
              src={state.thumbnailUrl}
              alt={isImage ? 'Image preview' : 'Video thumbnail'}
              className="mx-auto max-h-[300px] rounded-lg object-contain shadow-md"
            />
            <div className="mt-6 space-y-2 text-center">
              <p className="font-semibold text-primary">{state.file?.name}</p>
              <div className="flex items-center justify-center gap-4 text-sm text-secondary">
                <span>{formatFileSize(state.file?.size || 0)}</span>
                {isImage ? (
                  <>
                    <span>&bull;</span>
                    <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                      <Image className="h-3 w-3" />
                      IMG
                    </span>
                  </>
                ) : (
                  <>
                    <span>&bull;</span>
                    <span>{formatDuration(state.duration || 0)}</span>
                  </>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="absolute right-4 top-4"
              leftIcon={<X className="h-4 w-4" />}
            >
              Remove
            </Button>
          </div>
        ) : state.uploading ? (
          /* Processing */
          <div className="flex flex-col items-center gap-6 px-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/10">
              <Film className="h-8 w-8 animate-pulse text-brand" />
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-primary">Processing file...</p>
              <p className="text-sm text-secondary">
                Validating and preparing preview
              </p>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center gap-6 px-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/10">
              <Upload className="h-8 w-8 text-brand" />
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-primary">
                Drag and drop your creative here
              </p>
              <p className="text-sm text-secondary">or</p>
              <Button onClick={handleBrowseClick} variant="secondary" size="md">
                Browse Files
              </Button>
            </div>
            <div className="space-y-1 text-xs text-muted">
              <p>Supported formats: MP4, MOV, WebM, JPG, PNG, WebP</p>
              <p>
                Video: max {MAX_VIDEO_SIZE_MB}MB, {MAX_DURATION_SECONDS / 60} min
                {' '}&bull;{' '}
                Image: max {MAX_IMAGE_SIZE_MB}MB
              </p>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALL_ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      {/* Error Message */}
      {state.error && (
        <div className="flex items-start gap-3 rounded-lg bg-danger/10 p-4 text-sm text-danger">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>{state.error}</p>
        </div>
      )}
    </div>
  );
}
